import {createContext, useState, useEffect, useContext } from "react";
import { getImageUrl } from "../services/api";

const CartContext = createContext();
const CART_STORAGE_KEY = "cart_items";

const loadStoredCart = () => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// Les différentes pages construisent l'item panier avec des noms de champs
// différents (image / image_url / dish_image_url, restaurantName / restaurant_name...).
// On normalise ici une bonne fois pour toutes pour que l'image et le nom du
// restaurant s'affichent correctement partout où le panier est utilisé.
const normalizeCartItem = (dish) => {
  const rawImage = dish.image || dish.image_url || dish.dish_image_url || dish.imageUrl;
  const restaurantName =
    dish.restaurantName ||
    dish.restaurant_name ||
    (typeof dish.restaurant === 'string' ? dish.restaurant : dish.restaurant?.name);

  return {
    ...dish,
    image: getImageUrl(rawImage) || rawImage || null,
    restaurantName,
    restaurant_id: dish.restaurant_id || dish.restaurantId,
  };
};

function CartProvider({ children }) {
    const [cartItems, setCartItems] = useState(loadStoredCart);

    useEffect(() => {
      try {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
      } catch {
        // Stockage indisponible (navigation privée, quota dépassé...) : le panier reste
        // fonctionnel en mémoire pour la session en cours, simplement non persisté.
      }
    }, [cartItems]);

    const addToCart = (dish) => {
      const normalizedDish = normalizeCartItem(dish);
      setCartItems((prev) => {
        const supKey = (sups) => (sups || []).map(s => s.id).sort().join(',');
        const incomingKey = supKey(normalizedDish.supplements);
        const existing = prev.find(
          item => item.id === normalizedDish.id && supKey(item.supplements) === incomingKey
        );
        if (existing) {
          return prev.map(item =>
            item.id === normalizedDish.id && supKey(item.supplements) === incomingKey
              ? { ...item, quantity: item.quantity + (normalizedDish.quantity || 1) }
              : item
          );
        }
        return [...prev, { ...normalizedDish, quantity: normalizedDish.quantity || 1 }];
      });
    };

    const removeFromCart = (dish) => {
      setCartItems((prev) => {
        const existing = prev.find(item => item.id === dish.id);
        if (existing && existing.quantity > 1) {
          return prev.map(item =>
            item.id === dish.id ? { ...item, quantity: item.quantity - 1 } : item
          );
        }
        return prev.filter(item => item.id !== dish.id);
      });
    };

    const deleteFromCart = (dish) => {
      setCartItems((prev) => prev.filter(item => item.id !== dish.id));
    };

    const updateQuantity = (dishId, quantity) => {
      setCartItems((prev) =>
        prev.map(item =>
          item.id === dishId ? { ...item, quantity: Math.max(1, quantity) } : item
        )
      );
    };

    const updateCartItem = (dishId, updatedItem) => {
      const normalizedItem = normalizeCartItem(updatedItem);
      setCartItems((prev) =>
        prev.map(item =>
          item.id === dishId ? { ...normalizedItem, id: dishId } : item
        )
      );
    };

    const clearCart = () => {
      setCartItems([]);
    };
  
    // Calculer le total du panier (incluant les suppléments)
    const total = cartItems.reduce((sum, item) => {
      const itemPrice = parseFloat(item.price) || 0;
      const itemQuantity = parseInt(item.quantity) || 1;
      
      // Calculer le prix des suppléments pour cet item
      const supplementsTotal = item.supplements ? 
        item.supplements.reduce((supSum, supplement) => {
          const supPrice = parseFloat(supplement.price) || 0;
          const supQuantity = parseInt(supplement.quantity) || 1;
          return supSum + (supPrice * supQuantity);
        }, 0) : 0;
      
      // Total pour cet item = (prix de base + suppléments) * quantité
      const itemTotal = (itemPrice + supplementsTotal) * itemQuantity;
      return sum + itemTotal;
    }, 0);
    
    return (
      <CartContext.Provider value={{ 
        cartItems, 
        total, 
        addToCart, 
        updateQuantity, 
        removeFromCart, 
        deleteFromCart, 
        updateCartItem, 
        clearCart 
      }}>
        {children}
      </CartContext.Provider>
    );
  }

export const useCart = () => useContext(CartContext);

export default CartProvider;
